import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, MessageCircle } from "lucide-react";
import { HERO_IMAGE } from "@/lib/api";
import { CONTACT } from "@/lib/content";
import { useNavigate } from "react-router-dom";

/* ---------- Masked line-by-line reveal ---------- */
const LINE_EASE = [0.22, 1, 0.36, 1];
const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: LINE_EASE }}
    >
      {children}
    </motion.span>
  </span>
);

/* ---------- Kinetic rotating word ---------- */
const ROTATE_WORDS = ["Investment.", "Paint.", "Interior.", "Glass.", "Every Panel."];
function RotatingWord() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % ROTATE_WORDS.length), 2400);
    return () => clearInterval(t);
  }, []);
  // Longest word reserves the width so layout doesn't jitter
  const longest = ROTATE_WORDS.reduce((a, b) => (b.length > a.length ? b : a), "");
  return (
    <span className="relative inline-block align-baseline overflow-hidden pb-[0.08em]">
      {/* invisible ghost sets width */}
      <span className="invisible whitespace-nowrap" aria-hidden="true">
        {longest}
      </span>
      {ROTATE_WORDS.map((w, k) => (
        <motion.span
          key={k}
          className="absolute left-0 top-0 whitespace-nowrap will-change-transform"
          initial={false}
          animate={{
            y: k === i ? "0%" : k < i ? "-110%" : "110%",
            opacity: k === i ? 1 : 0,
          }}
          transition={{ duration: 0.9, ease: LINE_EASE }}
        >
          {w}
        </motion.span>
      ))}
    </span>
  );
}

/* ---------- Ticker (live clock, mini-metadata) ---------- */
function LiveTicker() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const fmt = now
    .toLocaleTimeString("en-CA", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "America/Edmonton",
    });
  return (
    <div className="font-mono-alt text-[10px] text-white/45 tabular-nums flex items-center gap-3">
      <span className="w-1.5 h-1.5 rounded-full bg-[#1F5EFF] animate-pulse" />
      <span>EDMONTON / AB</span>
      <span className="text-white/20">·</span>
      <span>{fmt}</span>
      <span className="text-white/20">·</span>
      <span>53.5461° N</span>
    </div>
  );
}

export default function KineticHero() {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, reduce ? 1.08 : 1.22]);
  const dimOpacity = useTransform(scrollYProgress, [0, 1], [0.6, 0.9]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-8%"]);

  return (
    <section
      id="home"
      ref={wrapRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden isolate pt-24 pb-16 flex items-center"
    >
      {/* Layer 1: photograph with parallax */}
      <motion.div
        className="absolute inset-0 z-0 will-change-transform"
        style={{ y: imgY, scale: imgScale }}
      >
        <img
          src={HERO_IMAGE}
          alt="Dark luxury automotive studio — WTD Detailing & Tint Edmonton"
          className="w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      </motion.div>

      {/* Layer 2: chromatic vignette + tint */}
      <motion.div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{ opacity: dimOpacity }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black" />
        <div className="absolute inset-0 grid-bg opacity-[0.22]" />
        <div className="absolute -left-40 top-1/3 w-[560px] h-[560px] rounded-full bg-[#1F5EFF]/12 blur-[120px]" />
        <div className="absolute right-[-10%] bottom-[-10%] w-[520px] h-[520px] rounded-full bg-[#1F5EFF]/10 blur-[140px]" />
      </motion.div>

      {/* Layer 3: fine noise/grain */}
      <div className="absolute inset-0 z-[2] pointer-events-none mix-blend-overlay opacity-[0.06] grain-noise" />

      {/* Corner registration ticks */}
      <div className="absolute inset-x-6 md:inset-x-10 top-24 z-[3] pointer-events-none flex items-center justify-between font-mono-alt text-[10px] text-white/40">
        <span>WTD · N53°33′</span>
        <span className="hidden sm:inline">SESSION / 26-07</span>
        <span>W113°30′</span>
      </div>

      {/* Layer 4: content */}
      <motion.div
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full"
        style={{ y: contentY }}
      >
        {/* eyebrow */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-10 h-px bg-[#1F5EFF]" />
          <span className="font-mono-alt text-[12px] text-[#1F5EFF]">
            Edmonton · Detailing & Tint Studio
          </span>
        </motion.div>

        {/* Kinetic H1 with masked lines */}
        <h1
          className="font-display font-light text-white
            text-[54px] leading-[0.92]
            sm:text-7xl
            md:text-[104px] md:leading-[0.9]
            lg:text-[124px] lg:leading-[0.88]
            xl:text-[148px] xl:leading-[0.86]
            tracking-[-0.035em] max-w-6xl"
        >
          <MaskLine delay={0.15}>Protect</MaskLine>
          <MaskLine delay={0.28}>
            <span className="text-white/85">Your </span>
            <RotatingWord />
          </MaskLine>
        </h1>

        <div className="mt-8 md:mt-12 grid md:grid-cols-[1fr_auto] gap-8 md:gap-16 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: LINE_EASE }}
            className="text-base md:text-lg text-white/60 max-w-xl font-light leading-relaxed"
          >
            Precision Ceramic Coating, Paint Correction & Detailing —{" "}
            <span className="text-white">crafted in Edmonton</span> under focused lighting, in a
            dust-free studio, by experienced installers. Now installing Paint Protection Film &amp;{" "}
            Windshield Protection Film.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
          >
            <LiveTicker />
          </motion.div>
        </div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35, ease: LINE_EASE }}
          className="mt-10 md:mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button
            data-testid="hero-cta-book"
            onClick={() => navigate("/book")}
            className="group inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full bg-white text-black font-medium tracking-tight transition-all duration-500 hover:bg-[#1F5EFF] hover:text-white will-change-transform"
          >
            <span className="text-[15px]">Book Now</span>
            <span className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center transition-transform duration-500 group-hover:rotate-45 group-hover:bg-white group-hover:text-black">
              <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </span>
          </button>
          <button
            data-testid="hero-cta-question"
            onClick={() => navigate("/contact")}
            className="inline-flex items-center gap-3 px-6 py-3.5 rounded-full border border-white/25 text-white/90 hover:border-white hover:bg-white/5 transition-all duration-300 text-[15px]"
          >
            Ask a Question
          </button>
          <a
            data-testid="hero-cta-whatsapp"
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full border border-white/25 text-white/90 hover:border-white hover:bg-white/5 transition-all duration-300 text-[15px]"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Us
          </a>
        </motion.div>

        {/* Stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 1.5 }}
          className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-5xl border-t border-white/10 pt-8"
        >
          {[
            { k: "9", v: "Detailing Services" },
            { k: "3", v: "Service Bays" },
            { k: "YEG", v: "Edmonton, Alberta" },
            { k: "$0", v: "Hidden Fees" },
          ].map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`} className="border-l border-white/10 pl-4">
              <div className="font-display text-3xl md:text-4xl text-white tabular-nums">{s.k}</div>
              <div className="mt-2 font-mono-alt text-[10px] text-white/45">{s.v}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Bottom-right mark */}
      <div className="absolute bottom-6 right-6 md:bottom-8 md:right-10 z-10 hidden md:flex items-center gap-2 text-white/45">
        <ShieldCheck className="w-4 h-4 text-[#1F5EFF]" />
        <span className="font-mono-alt text-[10px]">Experienced Local Studio</span>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40">
        <span className="font-mono-alt text-[9px]">SCROLL</span>
        <span className="relative block w-px h-10 overflow-hidden">
          <motion.span
            className="absolute inset-x-0 top-0 h-3 bg-[#1F5EFF]"
            initial={{ y: "-100%" }}
            animate={{ y: "300%" }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          />
          <span className="absolute inset-0 bg-white/12" />
        </span>
      </div>
    </section>
  );
}
